import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosConfig';

const MaintenanceCalculationForm = ({ initialData = null, isEdit = false }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        vehicle_id: initialData?.vehicle_id || '',
        engine_oil: initialData?.engine_oil || '',
        oil_and_filter: initialData?.oil_and_filter || '',
        km_10000: initialData?.km_10000 || '',
        km_20000: initialData?.km_20000 || '',
        km_30000: initialData?.km_30000 || '',
        km_40000: initialData?.km_40000 || '',
        km_50000: initialData?.km_50000 || '',
        km_60000: initialData?.km_60000 || '',
        km_70000: initialData?.km_70000 || '',
        km_80000: initialData?.km_80000 || '',
        km_90000: initialData?.km_90000 || '',
        km_100000: initialData?.km_100000 || ''
    });

    useEffect(() => {
        fetchVehicles();
    }, []);

    const fetchVehicles = async () => {
        try {
            const response = await axiosInstance.get('/vehicles');
            const data = response.data.data || response.data || [];
            setVehicles(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching vehicles:', error);
            toast.error('Failed to load vehicles');
        }
    };

    const validatePrice = (value) => {
        if (!value) return true;
        const priceRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
        return priceRegex.test(value);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        const newErrors = { ...errors };
        const priceFields = ['oil_and_filter', 'km_10000', 'km_20000', 'km_30000', 'km_40000', 'km_50000', 'km_60000', 'km_70000', 'km_80000', 'km_90000', 'km_100000'];

        if (priceFields.includes(name)) {
            if (value && !validatePrice(value)) {
                newErrors[name] = 'Must be a valid price';
            } else {
                delete newErrors[name];
            }
        }
        setErrors(newErrors);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields
        const newErrors = {};
        if (!formData.vehicle_id) newErrors.vehicle_id = 'Vehicle is required';
        if (!formData.engine_oil) newErrors.engine_oil = 'Engine oil type is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error('Please fix validation errors');
            return;
        }

        setLoading(true);
        try {
            if (isEdit && initialData?.id) {
                await axiosInstance.put(`/maintenance-calculator/${initialData.id}`, formData);
                toast.success('Maintenance record updated successfully!');
            } else {
                await axiosInstance.post('/maintenance-calculator', formData);
                toast.success('Maintenance record created successfully!');
            }
            navigate('/cms/maintenance-calculator');
        } catch (error) {
            console.error('Error saving maintenance record:', error);
            toast.error(error.response?.data?.message || 'Failed to save maintenance record');
        } finally {
            setLoading(false);
        }
    };

    const kmFields = [
        { name: 'km_10000', label: '10,000 km' },
        { name: 'km_20000', label: '20,000 km' },
        { name: 'km_30000', label: '30,000 km' },
        { name: 'km_40000', label: '40,000 km' },
        { name: 'km_50000', label: '50,000 km' },
        { name: 'km_60000', label: '60,000 km' },
        { name: 'km_70000', label: '70,000 km' },
        { name: 'km_80000', label: '80,000 km' },
        { name: 'km_90000', label: '90,000 km' },
        { name: 'km_100000', label: '100,000 km' }
    ];

    return (
        <Form onSubmit={handleSubmit}>
            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Vehicle</Form.Label>
                        <Form.Select
                            name="vehicle_id"
                            value={formData.vehicle_id}
                            onChange={handleInputChange}
                            isInvalid={!!errors.vehicle_id}
                        >
                            <option value="">Select Vehicle</option>
                            {vehicles.map((vehicle) => (
                                <option key={vehicle.id} value={vehicle.vehicle_id}>
                                    {vehicle.title_en}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.vehicle_id}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Engine Oil Type</Form.Label>
                        <Form.Control
                            type="text"
                            name="engine_oil"
                            value={formData.engine_oil}
                            onChange={handleInputChange}
                            placeholder="e.g., 0W-20"
                            isInvalid={!!errors.engine_oil}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.engine_oil}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Oil & Filter Price (SAR)</Form.Label>
                        <Form.Control
                            type="text"
                            name="oil_and_filter"
                            value={formData.oil_and_filter}
                            onChange={handleInputChange}
                            placeholder="e.g., 150.00"
                            isInvalid={!!errors.oil_and_filter}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.oil_and_filter}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <h5 className="mt-4 mb-3">Service Prices by Mileage (SAR)</h5>
            <Row>
                {kmFields.map((field) => (
                    <Col md={4} key={field.name}>
                        <Form.Group className="mb-3">
                            <Form.Label>{field.label}</Form.Label>
                            <Form.Control
                                type="text"
                                name={field.name}
                                value={formData[field.name]}
                                onChange={handleInputChange}
                                placeholder="e.g., 350.00"
                                isInvalid={!!errors[field.name]}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors[field.name]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                ))}
            </Row>

            <div className="text-end mt-4">
                <Button variant="secondary" className="me-2" onClick={() => navigate('/cms/maintenance-calculator')}>
                    Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                </Button>
            </div>
        </Form>
    );
};

export default MaintenanceCalculationForm;
